import { useEffect, useState, type FormEvent } from "react";
import type {
  AdminContractCancellationRevocationPreviewDto,
  ContractCancellationRevocationReasonCode
} from "@madamuh/domain";
import { useNavigate, useParams } from "react-router-dom";
import {
  previewAdminContractCancellationRevocation,
  requestAdminContractCancellationRevocation
} from "../../shared/api/commercialContract";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(value));
}

export function AdminContractCancellationRevocationPage() {
  const { tenantSlug = "", cancellationRequestId = "" } = useParams();
  const navigate = useNavigate();
  const { session, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [preview, setPreview] = useState<AdminContractCancellationRevocationPreviewDto | null>(null);
  const [reasonCode, setReasonCode] = useState<ContractCancellationRevocationReasonCode>("changedMind");
  const [reasonText, setReasonText] = useState("");
  const [confirmationText, setConfirmationText] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!isSessionReady) return;

    void previewAdminContractCancellationRevocation(tenantSlug, cancellationRequestId)
      .then(setPreview)
      .catch(() => setMessage("取消し条件を取得できませんでした。"));
  }, [cancellationRequestId, isSessionReady, tenantSlug]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!preview) return;
    const trimmedReasonText = reasonText.trim();
    if (reasonCode === "other" && !trimmedReasonText) {
      setMessage("その他を選んだ場合は理由の詳細を入力してください。");
      return;
    }

    try {
      await requestAdminContractCancellationRevocation(tenantSlug, cancellationRequestId, {
        cancellationRequestId,
        expectedCommercialStateVersion: preview.commercialStateVersion,
        expectedContractContactVersion: preview.contractContactVersion,
        expectedOriginalCancellationRequestVersion: preview.originalCancellationRequestVersion,
        expectedCalculationFingerprint: preview.calculationFingerprint,
        confirmationText,
        reasonCode,
        reasonText: reasonCode === "other" ? trimmedReasonText : null
      });
      navigate(`/t/${tenantSlug}/teacher/admin/contract`);
    } catch {
      setMessage("取消し確認メールを送信できませんでした。");
    }
  }

  const confirmationMatches =
    preview && confirmationText.trim().normalize("NFKC") === preview.tenantDisplayName.trim().normalize("NFKC");

  return (
    <OperatorPageScaffold
      currentArea="admin"
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      title="解約予約の取消し"
      description="現在の契約メールでの確認後に解約予約を取り消します。"
      links={[{ to: "/t/:tenantSlug/teacher/admin/contract", label: "契約・解約へ戻る" }]}
    >
      <form onSubmit={(event) => void handleSubmit(event)} style={{ display: "grid", gap: 12, maxWidth: 640 }}>
        {message ? <p>{message}</p> : null}
        {preview ? (
          <>
            <dl>
              <div><dt>利用終了予定日</dt><dd>{formatDate(preview.serviceEndsAt)}</dd></div>
              <div><dt>最初に利用しない月</dt><dd>{preview.firstSkippedServiceMonth}</dd></div>
              <div><dt>取消期限</dt><dd>{formatDate(preview.cancellationRevocableUntil)}</dd></div>
              <div><dt>復元後の契約状態</dt><dd>{preview.restoredContractStatus === "trialing" ? "無料期間中" : "契約中"}</dd></div>
            </dl>
            <p>{preview.billingRestorationSummary.message}</p>
            <label>
              理由
              <select
                value={reasonCode}
                onChange={(event) => {
                  const nextReasonCode = event.target.value as ContractCancellationRevocationReasonCode;
                  setReasonCode(nextReasonCode);
                  if (nextReasonCode !== "other") setReasonText("");
                }}
              >
                <option value="cancelledByMistake">誤って解約した</option>
                <option value="changedMind">考えが変わった</option>
                <option value="serviceStillNeeded">サービスを継続する</option>
                <option value="other">その他</option>
              </select>
            </label>
            {reasonCode === "other" ? (
              <label>
                理由の詳細
                <textarea
                  value={reasonText}
                  onChange={(event) => setReasonText(event.target.value)}
                  required
                  rows={4}
                />
              </label>
            ) : null}
            <label>
              <input type="checkbox" checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} />
              {" "}取消しを確定すると利用・請求が再開される場合があることを理解しました。
            </label>
            <label>
              クラブ名を入力
              <input value={confirmationText} onChange={(event) => setConfirmationText(event.target.value)} placeholder={preview.tenantDisplayName} />
            </label>
            <button disabled={!acknowledged || !confirmationMatches}>契約メールへ取消し確認を送る</button>
          </>
        ) : null}
      </form>
    </OperatorPageScaffold>
  );
}
